/**
 * Safe Zod parser for Prisma JSON columns.
 *
 * Prisma JSON columns carry no DB-level shape guarantees. One drifted row
 * must not 500 an endpoint — so we safeParse, log on mismatch, and return
 * null. Call sites already handle null (columns are nullable anyway).
 *
 * For reads where a shape violation means the request is semantically
 * undefined (auth claims, payment amounts, state-machine transitions),
 * call `schema.parse(value)` directly with a comment explaining why a
 * throw is correct — the terra/no-unsafe-schema-parse lint rule will
 * require a disable comment there.
 */

import { Prisma } from '@prisma/client';
import { z } from 'zod';
import { logger } from '../../utils/logger';
import { safeParseJson } from '../parse-json';

export interface DbJsonContext {
  /** `table.column` — e.g. `v4_action_executions.handler_result` */
  column: string;
  /** Row primary key for the drift alert */
  rowId?: string | number | bigint | null;
}

export function parseDbJson<T extends z.ZodTypeAny>(
  schema: T,
  value: unknown,
  ctx: DbJsonContext,
): z.infer<T> | null {
  if (value === null || value === undefined) return null;
  const result = schema.safeParse(value);
  if (!result.success) {
    logger.warn(
      {
        column: ctx.column,
        rowId: ctx.rowId ?? null,
        issues: result.error.issues,
      },
      'db_json_schema_mismatch',
    );
    return null;
  }
  return result.data;
}

/**
 * `parseDbJson` for JSON stored as TEXT (a `String?` column), where Prisma hands
 * back the raw string. Parses the text, then validates it, and logs the same
 * `db_json_schema_mismatch` entry as `parseDbJson` (plus `kind`: `syntax` for
 * text that is not JSON, `shape` for JSON that fails the schema). Null,
 * undefined and an empty string return null without a log, as an absent
 * column does for `parseDbJson`. Use `parseDbJson` for a real Json column.
 */
export function parseDbJsonText<T extends z.ZodTypeAny>(
  schema: T,
  text: string | null | undefined,
  ctx: DbJsonContext,
): z.infer<T> | null {
  if (text === null || text === undefined || text === '') return null;
  const result = safeParseJson(schema, text);
  if (!result.success) {
    logger.warn(
      {
        column: ctx.column,
        rowId: ctx.rowId ?? null,
        kind: result.error.kind,
        issues: result.error.issues,
      },
      'db_json_schema_mismatch',
    );
    return null;
  }
  return result.data;
}

export const FreeformObjectSchema = z.record(z.string(), z.unknown());

/**
 * Read a Json column that holds a free-form object (`Record<string, unknown>`),
 * treating null or any non-object value as `{}`.
 */
export function parseDbJsonObject(value: unknown, ctx: DbJsonContext): Record<string, unknown> {
  return parseDbJson(FreeformObjectSchema, value, ctx) ?? {};
}

/**
 * The write side of `parseDbJson`: a value typed from the column's Zod schema
 * (`z.infer`, per "Writer/reader drift prevention" in
 * docs/v4-system/db-json-parsing.md), as the `Prisma.InputJsonValue` a Json
 * column write takes. Prisma's input type cannot see through `unknown`-valued
 * records (raw Salesforce rows, handler results), so the ONE cast lives here
 * rather than at each writer. Pass a value whose schema the reader will parse
 * it back with — nothing here validates.
 */
export function toDbJson(value: object): Prisma.InputJsonValue {
  return value as Prisma.InputJsonValue;
}

/**
 * Re-write a Json value READ from one Prisma client into a REQUIRED Json
 * column on another (e.g. cloning rows between environments). A stored JSON
 * `null` becomes `Prisma.JsonNull`. The return type excludes `Prisma.DbNull`,
 * which a required column rejects at compile time, so the nullable variant's
 * result cannot be passed here by mistake. A bare `null` is rejected at the
 * TYPE level only; a nullable Json column accepts it at runtime and stores
 * JSON null, which `Prisma.JsonNull` reproduces explicitly.
 */
export function toRequiredJsonInput(value: Prisma.JsonValue): Prisma.InputJsonValue | typeof Prisma.JsonNull {
  return value === null ? Prisma.JsonNull : value;
}

/**
 * Same as `toRequiredJsonInput` for a NULLABLE Json column: a `null` read
 * becomes SQL NULL (`Prisma.DbNull`). The return type includes `DbNull`, so
 * the result only type-checks against a nullable column. Prisma's read type
 * `JsonValue` already contains `null`, so the two helpers cannot be told apart
 * by parameter type; the return types carry the distinction.
 */
export function toNullableJsonInput(value: Prisma.JsonValue): Prisma.InputJsonValue | typeof Prisma.DbNull {
  return value === null ? Prisma.DbNull : value;
}
